/* eslint-disable complexity */
import { camelToTitle } from '@redviking/argonaut-util/src/mes/appletUtil';
import { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { ValidationResult } from 'types';
import { screensTabRoute } from '../util/consts';

const invalidGTZeroError = (payload: {
    item: string;
    screenId: string;
    screenName: string;
    aspectRatio: string;
}): (ValidationResult & { status: 'error' }) => ({
    status: 'error',
    routeName: screensTabRoute,
    errorMessage: `${payload.item} must be greater than 0 in screen ${payload.screenName}:${camelToTitle(payload.aspectRatio)}`,
    accessorActions: accessor => {
        accessor.appletDesign.patchAppletDesignState({
            activeScreenId: payload.screenId,
        });
    },
});

const invalidVarSourceError = (payload: {
    item: string;
    screenId: string;
    screenName: string;
    aspectRatio: string;
}): (ValidationResult & { status: 'error' }) => ({
    status: 'error',
    routeName: screensTabRoute,
    errorMessage: `${payload.item} must be linked to a variable if using variable input in screen ${payload.screenName}:${camelToTitle(payload.aspectRatio)}`,
    accessorActions: accessor => {
        accessor.appletDesign.patchAppletDesignState({
            activeScreenId: payload.screenId,
        });
    },
});

export const genericScreenDesignerCellValidations = (payload: {
    screen: Latest.Screen.Config;
    aspectRatio: Latest.Screen.Designer.GridLayoutAspectRatio;
    cellItem: Latest.Screen.Designer.LinkedGridCell | Latest.Screen.Designer.GridLayout['gridConfig'];
    allAppletVariables: Record<string, Latest.AppletVariables.AppletVariable | Latest.AppletVariables.GeneratedAppletVariable>;
}): ValidationResult => {
    if ('rows' in payload.cellItem && 'columns' in payload.cellItem) {
        if (payload.cellItem.columns.find(c => c.weight <= 0) || payload.cellItem.rows.find(r => r.weight <= 0)) {
            return invalidGTZeroError({
                screenId: payload.screen.id,
                item: 'Row and column weights',
                screenName: payload.screen.name,
                aspectRatio: payload.aspectRatio,
            });
        }
    }
    if (payload.cellItem.elevation < 0) {
        return invalidGTZeroError({
                screenId: payload.screen.id,
                item: 'Elevation',
                screenName: payload.screen.name,
                aspectRatio: payload.aspectRatio,
            });
    }
    if (payload.cellItem.borderRadius < 0) {
        return invalidGTZeroError({
                screenId: payload.screen.id,
                item: 'Border radiu',
                screenName: payload.screen.name,
                aspectRatio: payload.aspectRatio,
            });
    }
    if (payload.cellItem.margin.top < 0 || payload.cellItem.margin.right < 0 || payload.cellItem.margin.bottom < 0 || payload.cellItem.margin.left < 0) {
        return invalidGTZeroError({
                screenId: payload.screen.id,
                item: 'Margin',
                screenName: payload.screen.name,
                aspectRatio: payload.aspectRatio,
            });
    }
    if (payload.cellItem.padding.top < 0 || payload.cellItem.padding.right < 0 || payload.cellItem.padding.bottom < 0 || payload.cellItem.padding.left < 0) {
        return invalidGTZeroError({
                screenId: payload.screen.id,
                item: 'Padding',
                screenName: payload.screen.name,
                aspectRatio: payload.aspectRatio,
            });
    }
    if (payload.cellItem.border.top.width < 0 || payload.cellItem.border.top.width < 0 || payload.cellItem.border.top.width < 0 || payload.cellItem.border.top.width < 0) {
        return invalidGTZeroError({
                screenId: payload.screen.id,
                item: 'Border width',
                screenName: payload.screen.name,
                aspectRatio: payload.aspectRatio,
            });
    }

    const backgroundColorVarOrConst = payload.cellItem.backgroundColor;
    if (backgroundColorVarOrConst.type === 'var') {
        if (!backgroundColorVarOrConst.varId) {
            return invalidVarSourceError({
                screenId: payload.screen.id,
                item: 'Background color',
                screenName: payload.screen.name,
                aspectRatio: payload.aspectRatio,
            });
        }
        /**
         * FUTURE: Once variable providers declare what the type of their outputs are we can use that to validate that the variable controlling background color is a string and not a number
         * NOTE: Currently returns a string representing the variable name. Sould return var config which contains the type
         */
        const linkedVariable = payload.allAppletVariables[backgroundColorVarOrConst.varId];
        if (!linkedVariable) {
            return invalidVarSourceError({
                screenId: payload.screen.id,
                item: 'Background color',
                screenName: payload.screen.name,
                aspectRatio: payload.aspectRatio,
            });
        }
    }

    if ('type' in payload.cellItem) {
        if (payload.cellItem.type === 'infoBox') {
            const typedCellItem = payload.cellItem as Latest.Screen.Designer.InfoBoxCell;
            if (typedCellItem.additionalCellProperties.contentFontSize < 0) {
                return invalidGTZeroError({
                    screenId: payload.screen.id,
                    item: 'Content font size',
                    screenName: payload.screen.name,
                    aspectRatio: payload.aspectRatio,
                });
            }
            if (typedCellItem.additionalCellProperties.headerFontSize < 0) {
                return invalidGTZeroError({
                    screenId: payload.screen.id,
                    item: 'Header font size',
                    screenName: payload.screen.name,
                    aspectRatio: payload.aspectRatio,
                });
            }
            const contentVarOrConst = typedCellItem.additionalCellProperties.contentSourceVar;
            if (contentVarOrConst.type === 'var') {
                if (!contentVarOrConst.varId) {
                    return invalidVarSourceError({
                        screenId: payload.screen.id,
                        item: 'Infobox Content',
                        screenName: payload.screen.name,
                        aspectRatio: payload.aspectRatio,
                    });
                }
            }
            const headerVarOrConst = typedCellItem.additionalCellProperties.headerSourceVar;
            if (headerVarOrConst.type === 'var') {
                if (!headerVarOrConst.varId) {
                    return invalidVarSourceError({
                        screenId: payload.screen.id,
                        item: 'Infobox Header',
                        screenName: payload.screen.name,
                        aspectRatio: payload.aspectRatio,
                    });
                }
            }
        }
        if (payload.cellItem.type === 'tabInstance') {
            const typedCellItem = payload.cellItem as Latest.Screen.Designer.TabInstanceCell;
            if (!typedCellItem.additionalCellProperties.label) {
                return {
                    status: 'error',
                    routeName: screensTabRoute,
                    errorMessage: `Tab instance must have a label in screen ${payload.screen.name}:${camelToTitle(payload.aspectRatio)}`,
                    accessorActions: accessor => {
                        accessor.appletDesign.patchAppletDesignState({
                            activeScreenId: payload.screen.id,
                        });
                    },
                };
            }
        }
    }

    return {
        status: 'success',
    };
};
